import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Subject, combineLatest } from 'rxjs';
import { UserSyncHandlerComponent } from './components/user-sync-handler/user-sync-handler.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { AuthService } from './auth.service';
import { PermissionService } from './services/permission.service';
import { UserProfile } from './models/user-management.model';
import { extractUserInfo } from './utils/user-claims-extractor';
import { ToastService } from './services/toast.service';
import { DisplayPreferencesService } from './services/display-preferences.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    UserSyncHandlerComponent,
    PageHeaderComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit, OnDestroy {
  isDashboard = false;
  isLandingPage = false;
  currentUser: UserProfile | null = null;
  private destroy$ = new Subject<void>();
  private isInitializingProfile = false;

  constructor(
    private router: Router,
    private auth: AuthService,
    private permissionService: PermissionService,
    private toastService: ToastService,
    private displayPreferences: DisplayPreferencesService
  ) {
    // Check initial route
    this.updateDashboardState(this.router.url);

    // Update on route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        this.updateDashboardState(event.url);
      });
  }

  ngOnInit(): void {
    // Restore the signed-in user's theme/density (Settings → Display) on reload.
    this.displayPreferences.loadForCurrentUser();

    // The profile (and with it the whole permission context) needs BOTH conditions:
    //   1. the backend session is registered  → isSessionReady$
    //   2. an MSAL account is resolvable      → auth.waitForAccount()
    // Waiting on both, rather than reading auth.user once and giving up, is what stops a
    // slow/racy MSAL account from permanently leaving the user with zero permissions.
    combineLatest([
      this.permissionService.isSessionReady$.pipe(filter(ready => ready)),
      this.auth.waitForAccount(),
    ])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        // Re-run once the session is registered: on a fresh login the email is
        // only in sessionStorage by now, so the first call above found nothing.
        this.displayPreferences.loadForCurrentUser();
        this.initializeUserProfile();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateDashboardState(url: string): void {
    // Dashboard is the root path '/'
    this.isDashboard = url === '/' || url === '';
    this.isLandingPage = url === '/landing-page';
  }

  private initializeUserProfile(): void {
    // Prevent multiple simultaneous initialization calls
    if (this.isInitializingProfile) {
      return;
    }

    if (!this.auth.user) {
      console.warn('⚠️ [Layout] No auth user, cannot initialize profile');
      return;
    }
    
    this.isInitializingProfile = true;
    
    // Extract user info
    const userInfo = extractUserInfo(this.auth.user);
    const email = userInfo?.email || 
                 (this.auth.user as any)?.username || 
                 (this.auth.user as any)?.preferred_username ||
                 (this.auth.user as any)?.idTokenClaims?.preferred_username ||
                 (this.auth.user as any)?.idTokenClaims?.email || '';
        
    // Check if user is authenticated before making API calls
    if (!this.auth.isAuthenticated || !email) {
      this.setMockUser(email);
      this.isInitializingProfile = false;
      return;
    }

    // User is authenticated - make API calls.
    // Transient failures are retried: without this a single blip on either IAM call left
    // the permission context empty for the whole session (every gated button disabled).
    this.permissionService.loadUsersAndRoles().subscribe({
      next: ({ users, roles }) => {        
        let profile = users.find((u: UserProfile) => {
          const userEmail = u.email?.toLowerCase() || '';
          const searchEmail = email.toLowerCase();
          return userEmail === searchEmail;
        });
        
        if (profile) {
          console.log('🔍 [Layout] Found user profile:', profile.email);
          console.log('🔍 [Layout] User roles before enrichment:', profile.roles.map(r => r.name));
          
          // ENRICH USER ROLES: Match role names with full role data from /api/roles
          if (profile.roles.length > 0) {
            const enrichedRoles = profile.roles.map(userRole => {
              // Find the full role data from /api/roles by matching name
              const normalizedUserRoleName = userRole.name.toUpperCase().trim().replace(/[\s-]+/g, '_');
              const fullRole = roles.find(r => {
                const normalizedRoleName = r.name.toUpperCase().trim().replace(/[\s-]+/g, '_');
                return normalizedRoleName === normalizedUserRoleName;
              });
              
              if (fullRole) {
                return fullRole;
              }
              return userRole;
            });
            
            profile = {
              ...profile,
              roles: enrichedRoles
            };
          }
          
          this.currentUser = profile;
          this.permissionService.setUserProfile(profile);
        } else {
          console.warn('⚠️ [Layout] User profile not found, using mock data');
          this.setMockUser(email);
        }
        this.isInitializingProfile = false;
      },
      error: (error: any) => {
        console.error('❌ [Layout] Failed to initialize user profile:', error);
        this.setMockUser(email);
        this.isInitializingProfile = false;
        // The retries are exhausted, so the permission context stays empty and gated
        // controls will render disabled. Say so instead of leaving the user to guess
        // why their buttons stopped working.
        this.toastService.error(
          'Could not load your permissions',
          'Some actions may appear disabled. Please refresh the page.'
        );
      }
    });
  }

  private setMockUser(email: string): void {
    this.currentUser = {
      id: '1',
      azureId: 'mock-azure-id',
      email: email || 'anonymous@example.com',
      displayName: 'Mock User',
      givenName: 'Mock',
      surname: 'User',
      roles: [],
      isActive: true,
      lastLoginAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }
}
